import * as React from "react";
import { displayErrorToast } from "src/components/shared/Toasts/Toast";
import Tooltip from "src/components/shared/Tooltip";
import { ToastIds } from "src/enums/errorToastIds";
import { UserRole } from "src/enums/user/userRoles";
import { useGameContext } from "src/game/useGameContext";
import useIsMobile from "src/hooks/useIsMobile";
import {
  TimerEndedTooltip,
  TimerIdleMenu,
  TimerStartedMenu,
} from "./components";
import {
  PieTimer,
  StopWatchIcon,
  timerEndedTooltipWrapperStyles,
  timerMenuWrapperStyles,
  TimerWrapper,
} from "./Timer.components";

const minToMs = (minutes: number) => minutes * 1000 * 60;

const Timer: React.FunctionComponent = () => {
  const [isMenuVisible, setIsMenuVisible] = React.useState(false);
  const [isTimerEndedTooltipVisible, setIsTimerEndedTooltipVisible] =
    React.useState(false);
  const [timerMinutesInputValue, setTimerMinutesInputValue] =
    React.useState("1");
  const {
    gameState: { timer, user, game, token },
    actionHandlers: { createTimer, addTime, cancelTime, resetTimer },
  } = useGameContext();

  const [pieTimerAnimationDuration, setPieTimerAnimationDuration] =
    React.useState(0);
  const [pieTimerAnimationElapsed, setPieTimerAnimationElapsed] =
    React.useState(0);

  const isTimerRunning = !!timer && !timer.isFinished;
  const canInteractWithTimer = user?.role === UserRole.OWNER;
  const isMobile = useIsMobile();

  const timerElRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!timer) {
      return;
    }

    setIsTimerEndedTooltipVisible(false);

    if (timer.isFinished && !isMenuVisible) {
      setIsTimerEndedTooltipVisible(true);
    }

    setPieTimerAnimationDuration(timer.willFinishAt - timer.haveStartedAt);
    setPieTimerAnimationElapsed(Date.now() - timer.haveStartedAt);
  }, [timer]);

  const handleTimerClick = () => {
    if (!canInteractWithTimer) {
      return;
    }

    setIsMenuVisible(val => !val);
    setIsTimerEndedTooltipVisible(false);
  };

  const startTimer = async () => {
    if (!game || !token) {
      return;
    }

    try {
      const response = await createTimer({
        gameId: game?.id,
        token,
        timer: { time: minToMs(parseFloat(timerMinutesInputValue)) },
      });
      if (response.error) {
        displayErrorToast(
          `Failed to start timer ${response.error.message}`,
          ToastIds.TIMER_START_FAILED
        );
      }
    } catch (err) {
      displayErrorToast("Failed to start timer", ToastIds.TIMER_START_FAILED);
    }
  };

  const add30SecondsHandler = async () => {
    if (!game || !token) {
      return;
    }

    try {
      const response = await addTime({
        gameId: game?.id,
        token,
        timer: { time: minToMs(0.5) },
      });
      if (response.error) {
        displayErrorToast(
          `Failed to add time ${response.error.message}`,
          ToastIds.TIMER_ADD_TIME_FAILED
        );
      }
    } catch (err) {
      displayErrorToast("Failed to add time", ToastIds.TIMER_ADD_TIME_FAILED);
    }
  };

  const restartTimerHandler = async () => {
    if (!game || !token) {
      return;
    }

    try {
      setIsMenuVisible(false);
      const response = await resetTimer({
        gameId: game?.id,
        token,
      });
      if (response.error) {
        displayErrorToast(
          `Failed to restart timer ${response.error.message}`,
          ToastIds.TIMER_RESTRT_FAILED
        );
      }
    } catch (err) {
      displayErrorToast(
        "Failed to restart timer",
        ToastIds.TIMER_RESTRT_FAILED
      );
    }
  };

  const cancelTimerHandler = async () => {
    if (!game || !token) {
      return;
    }

    try {
      const response = await cancelTime({
        gameId: game?.id,
        token,
      });
      if (response.error) {
        displayErrorToast(
          `Failed to cancel timer ${response.error.message}`,
          ToastIds.TIMER_CANCEL_FAILED
        );
      }
    } catch (err) {
      displayErrorToast("Failed to cancel timer", ToastIds.TIMER_CANCEL_FAILED);
    }
  };

  const onMinutesValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const re = /^[0-9\b]{1,2}$/;

    // if value is not blank, then test the regex

    if (e.target.value === "" || re.test(e.target.value)) {
      setTimerMinutesInputValue(e.target.value);
    }
  };

  const IdleMenu = (
    <TimerIdleMenu
      minutesValue={timerMinutesInputValue || ""}
      onMinutesValueChange={onMinutesValueChange}
      onStartTimer={() => {
        startTimer();
        setIsMenuVisible(false);
      }}
    />
  );

  return (
    <>
      <TimerWrapper
        onClick={handleTimerClick}
        canInteract={canInteractWithTimer}
        isActive={isMenuVisible}
        ref={timerElRef}>
        {isTimerRunning ? (
          <PieTimer
            $animationDurationInMs={pieTimerAnimationDuration}
            $elapsedTimeInMs={pieTimerAnimationElapsed}
            key={timer?.willFinishAt}
          />
        ) : (
          <StopWatchIcon />
        )}
      </TimerWrapper>

      <Tooltip
        visible={isMenuVisible}
        reference={timerElRef}
        onClickOutside={() => setIsMenuVisible(false)}
        arrow={false}
        placement="bottom-start"
        wrapperCssHelperStyles={timerMenuWrapperStyles}
        content={
          !timer || timer.isFinished ? (
            IdleMenu
          ) : (
            <TimerStartedMenu
              onAdd30Sec={add30SecondsHandler}
              onRestartTimer={restartTimerHandler}
              onCancelTimer={cancelTimerHandler}
            />
          )
        }
      />
      <Tooltip
        visible={isTimerEndedTooltipVisible}
        reference={timerElRef}
        borderThemeColor="error"
        placement={isMobile ? "bottom-start" : "right"}
        wrapperCssHelperStyles={timerEndedTooltipWrapperStyles}
        content={
          <TimerEndedTooltip
            onClose={() => setIsTimerEndedTooltipVisible(false)}
            onRestart={
              canInteractWithTimer
                ? () => {
                    startTimer();
                    setIsTimerEndedTooltipVisible(false);
                  }
                : undefined
            }
          />
        }
      />
    </>
  );
};

export default Timer;
